import { useEffect, useState, useCallback } from "react";
import { toast } from "sonner";
import { Plus, Save, Trash2, X, Pencil, Eye, EyeOff, MapPin, Building2, Square, Route, Settings as Cog, Crosshair, ChevronUp, ChevronDown } from "lucide-react";
import api, { formatApiErrorDetail } from "@/lib/api";
import AdminCard from "./AdminCard";
import MapPreview from "./MapPreview";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { ICON_NAMES, resolveIcon } from "@/lib/mapIcons";

const STATUSES = [
    { value: "available", label: "Dostępne" },
    { value: "alert", label: "Uwaga" },
    { value: "info", label: "Info" },
];

const SUBTABS = [
    { key: "pois", label: "Punkty", icon: MapPin },
    { key: "buildings", label: "Budynki", icon: Building2 },
    { key: "zones", label: "Strefy", icon: Square },
    { key: "paths", label: "Ścieżki", icon: Route },
    { key: "settings", label: "Ustawienia", icon: Cog },
];

// =====================================================================
// POI editor
// =====================================================================
function PoiForm({ initial, categories, draftPosition, onPickFromMap, onCancel, onSaved }) {
    const isEdit = !!initial?.id;
    const [form, setForm] = useState(() => ({
        label: initial?.label || "",
        details: initial?.details || "",
        left: initial?.left ?? draftPosition?.left ?? 50,
        top: initial?.top ?? draftPosition?.top ?? 50,
        icon: initial?.icon || "MapPin",
        color: initial?.color || "#0D4F3C",
        status: initial?.status || "available",
        status_text: initial?.status_text || "",
        category_key: initial?.category_key || "",
        visible: initial?.visible !== false,
        order: initial?.order ?? 0,
    }));
    const [saving, setSaving] = useState(false);

    // Reflect map clicks into the form (in both create AND edit mode)
    useEffect(() => {
        if (draftPosition) {
            setForm((f) => ({ ...f, left: draftPosition.left, top: draftPosition.top }));
        }
    }, [draftPosition]);

    const submit = async (e) => {
        e.preventDefault();
        if (!form.label.trim()) { toast.error("Podaj nazwę punktu."); return; }
        setSaving(true);
        try {
            const payload = { ...form, label: form.label.trim(), details: (form.details || "").trim(), status_text: (form.status_text || "").trim() };
            if (isEdit) {
                await api.put(`/map/pois/${initial.id}`, payload);
                toast.success("Punkt zaktualizowany.");
            } else {
                await api.post("/map/pois", payload);
                toast.success("Punkt dodany.");
            }
            onSaved();
        } catch (err) {
            toast.error(formatApiErrorDetail(err.response?.data?.detail));
        } finally { setSaving(false); }
    };

    const Preview = resolveIcon(form.icon);

    return (
        <form onSubmit={submit} className="space-y-4" data-testid="map-poi-form">
            <div className="grid md:grid-cols-2 gap-3">
                <div className="space-y-2">
                    <Label>Nazwa *</Label>
                    <Input required value={form.label} onChange={(e) => setForm({ ...form, label: e.target.value })} placeholder="Np. Plac zabaw" data-testid="map-poi-label" />
                </div>
                <div className="space-y-2">
                    <Label>Kategoria</Label>
                    <select
                        value={form.category_key}
                        onChange={(e) => setForm({ ...form, category_key: e.target.value })}
                        className="flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm"
                        data-testid="map-poi-category"
                    >
                        <option value="">— bez kategorii —</option>
                        {categories.map((c) => <option key={c.key} value={c.key}>{c.label}</option>)}
                    </select>
                </div>
            </div>

            <div className="space-y-2">
                <Label>Opis (widoczny w okienku po kliknięciu)</Label>
                <Textarea rows={3} value={form.details} onChange={(e) => setForm({ ...form, details: e.target.value })} data-testid="map-poi-details" />
            </div>

            <div className="grid md:grid-cols-3 gap-3">
                <div className="space-y-2">
                    <Label className="flex items-center gap-2">Pozycja na mapie {!isEdit && <span className="text-[10px] text-gray-400">(kliknij na mapę po lewej)</span>}</Label>
                    <div className="grid grid-cols-2 gap-2">
                        <Input type="number" step="any" min={0} max={100} value={form.left} onChange={(e) => setForm({ ...form, left: parseFloat(e.target.value) || 0 })} placeholder="left %" data-testid="map-poi-left" />
                        <Input type="number" step="any" min={0} max={100} value={form.top} onChange={(e) => setForm({ ...form, top: parseFloat(e.target.value) || 0 })} placeholder="top %" data-testid="map-poi-top" />
                    </div>
                    {onPickFromMap && (
                        <Button type="button" variant="outline" size="sm" className="rounded-full text-xs" onClick={onPickFromMap} data-testid="map-poi-pick">
                            <Crosshair size={12} className="mr-1" /> Wskaż na mapie
                        </Button>
                    )}
                </div>

                <div className="space-y-2">
                    <Label>Status</Label>
                    <select
                        value={form.status}
                        onChange={(e) => setForm({ ...form, status: e.target.value })}
                        className="flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm"
                        data-testid="map-poi-status"
                    >
                        {STATUSES.map((s) => <option key={s.value} value={s.value}>{s.label}</option>)}
                    </select>
                    {form.status === "alert" && (
                        <Input
                            value={form.status_text}
                            onChange={(e) => setForm({ ...form, status_text: e.target.value })}
                            placeholder="Treść alertu (np. Odbiór 28.05.2026)"
                            data-testid="map-poi-status-text"
                        />
                    )}
                </div>

                <div className="space-y-2">
                    <Label>Kolor markera</Label>
                    <div className="flex items-center gap-2">
                        <input type="color" value={form.color} onChange={(e) => setForm({ ...form, color: e.target.value })} className="h-10 w-12 rounded border border-gray-200" data-testid="map-poi-color" />
                        <Input value={form.color} onChange={(e) => setForm({ ...form, color: e.target.value })} className="flex-1 font-mono text-xs" />
                    </div>
                </div>
            </div>

            <div className="space-y-2">
                <Label>Ikona</Label>
                <div className="grid grid-cols-6 sm:grid-cols-8 md:grid-cols-12 gap-1.5 max-h-48 overflow-y-auto p-2 bg-gray-50 rounded-xl border border-gray-100" data-testid="map-poi-icon-grid">
                    {ICON_NAMES.map((name) => {
                        const I = resolveIcon(name);
                        const active = form.icon === name;
                        return (
                            <button
                                type="button"
                                key={name}
                                onClick={() => setForm({ ...form, icon: name })}
                                className={`aspect-square rounded-lg flex items-center justify-center transition-colors ${
                                    active ? "ring-2 ring-[#D4A537]" : "hover:bg-white"
                                }`}
                                style={active ? { backgroundColor: form.color } : { backgroundColor: "white" }}
                                title={name}
                                data-testid={`map-poi-icon-${name}`}
                            >
                                <I size={16} className={active ? "text-white" : "text-gray-600"} />
                            </button>
                        );
                    })}
                </div>
                <div className="flex items-center gap-2 text-xs text-gray-500">
                    Podgląd:
                    <span className="inline-flex items-center justify-center w-7 h-7 rounded-full ring-2 ring-white shadow" style={{ backgroundColor: form.color }}>
                        <Preview size={12} className="text-white" strokeWidth={2.5} />
                    </span>
                </div>
            </div>

            <div className="grid md:grid-cols-2 gap-3">
                <label className="flex items-center gap-2 p-3 rounded-xl border border-gray-200 cursor-pointer hover:border-[#0D4F3C]/40">
                    <input type="checkbox" checked={form.visible} onChange={(e) => setForm({ ...form, visible: e.target.checked })} className="w-4 h-4 accent-[#0D4F3C]" data-testid="map-poi-visible" />
                    <div>
                        <div className="text-sm font-medium text-[#092E22]">Widoczny na stronie</div>
                        <div className="text-[11px] text-gray-500">Możesz tymczasowo ukryć bez usuwania</div>
                    </div>
                </label>
                <div className="space-y-1">
                    <Label className="text-xs">Kolejność sortowania</Label>
                    <Input type="number" value={form.order} onChange={(e) => setForm({ ...form, order: parseInt(e.target.value) || 0 })} data-testid="map-poi-order" />
                </div>
            </div>

            <div className="flex justify-end gap-2 pt-2 border-t border-gray-50">
                <Button type="button" variant="outline" onClick={onCancel} className="rounded-full" data-testid="map-poi-cancel">
                    <X size={14} className="mr-1.5" /> Anuluj
                </Button>
                <Button type="submit" disabled={saving} className="rounded-full bg-[#0D4F3C] hover:bg-[#092E22] text-white" data-testid="map-poi-save">
                    <Save size={14} className="mr-1.5" /> {saving ? "Zapisywanie..." : (isEdit ? "Zapisz" : "Dodaj punkt")}
                </Button>
            </div>
        </form>
    );
}

// =====================================================================
// Building editor
// =====================================================================
function BuildingForm({ initial, draftPosition, onPickFromMap, onCancel, onSaved }) {
    const isEdit = !!initial?.id;
    const [form, setForm] = useState(() => ({
        building_id: initial?.building_id || "",
        label: initial?.label || "",
        left: initial?.left ?? draftPosition?.left ?? 50,
        top: initial?.top ?? draftPosition?.top ?? 50,
        apartments: initial?.apartments ?? 0,
        klatki: initial?.klatki?.join(", ") || "",
        notes: initial?.notes || "",
        order: initial?.order ?? 0,
    }));
    const [saving, setSaving] = useState(false);

    useEffect(() => {
        if (draftPosition) setForm((f) => ({ ...f, left: draftPosition.left, top: draftPosition.top }));
    }, [draftPosition]);

    const submit = async (e) => {
        e.preventDefault();
        setSaving(true);
        try {
            const payload = {
                building_id: form.building_id.trim(),
                label: form.label.trim(),
                left: form.left, top: form.top,
                apartments: parseInt(form.apartments) || 0,
                klatki: form.klatki.split(",").map((k) => k.trim()).filter(Boolean),
                notes: form.notes.trim(),
                order: form.order,
            };
            if (isEdit) {
                await api.put(`/map/buildings/${initial.id}`, payload);
                toast.success("Budynek zaktualizowany.");
            } else {
                await api.post("/map/buildings", payload);
                toast.success("Budynek dodany.");
            }
            onSaved();
        } catch (err) {
            toast.error(formatApiErrorDetail(err.response?.data?.detail));
        } finally { setSaving(false); }
    };

    return (
        <form onSubmit={submit} className="space-y-4" data-testid="map-building-form">
            <div className="grid md:grid-cols-2 gap-3">
                <div className="space-y-2">
                    <Label>Identyfikator * (np. 25C)</Label>
                    <Input required value={form.building_id} onChange={(e) => setForm({ ...form, building_id: e.target.value })} data-testid="map-building-id" />
                </div>
                <div className="space-y-2">
                    <Label>Pełna nazwa *</Label>
                    <Input required value={form.label} onChange={(e) => setForm({ ...form, label: e.target.value })} placeholder="Aluzyjna 25C" data-testid="map-building-label" />
                </div>
            </div>
            <div className="grid md:grid-cols-3 gap-3">
                <div className="space-y-2">
                    <Label>Pozycja (left/top %)</Label>
                    <div className="grid grid-cols-2 gap-2">
                        <Input type="number" step="any" min={0} max={100} value={form.left} onChange={(e) => setForm({ ...form, left: parseFloat(e.target.value) || 0 })} />
                        <Input type="number" step="any" min={0} max={100} value={form.top} onChange={(e) => setForm({ ...form, top: parseFloat(e.target.value) || 0 })} />
                    </div>
                    {onPickFromMap && (
                        <Button type="button" variant="outline" size="sm" className="rounded-full text-xs" onClick={onPickFromMap}>
                            <Crosshair size={12} className="mr-1" /> Wskaż na mapie
                        </Button>
                    )}
                </div>
                <div className="space-y-2">
                    <Label>Liczba mieszkań</Label>
                    <Input type="number" value={form.apartments} onChange={(e) => setForm({ ...form, apartments: e.target.value })} />
                </div>
                <div className="space-y-2">
                    <Label>Klatki (oddzielone przecinkiem)</Label>
                    <Input value={form.klatki} onChange={(e) => setForm({ ...form, klatki: e.target.value })} placeholder="C1, C2" />
                </div>
            </div>
            <div className="space-y-2">
                <Label>Notatki dodatkowe</Label>
                <Textarea rows={2} value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} placeholder="Np. wejście od strony parkingu" />
            </div>
            <div className="flex justify-end gap-2 pt-2 border-t border-gray-50">
                <Button type="button" variant="outline" onClick={onCancel} className="rounded-full">
                    <X size={14} className="mr-1.5" /> Anuluj
                </Button>
                <Button type="submit" disabled={saving} className="rounded-full bg-[#0D4F3C] hover:bg-[#092E22] text-white">
                    <Save size={14} className="mr-1.5" /> {saving ? "Zapisywanie..." : (isEdit ? "Zapisz" : "Dodaj budynek")}
                </Button>
            </div>
        </form>
    );
}

// =====================================================================
// Zone editor
// =====================================================================
function ZoneForm({ initial, onCancel, onSaved }) {
    const isEdit = !!initial?.id;
    const [form, setForm] = useState(() => ({
        label: initial?.label || "",
        left: initial?.left ?? 30,
        top: initial?.top ?? 30,
        width: initial?.width ?? 30,
        height: initial?.height ?? 20,
        color: initial?.color || "#D4A537",
        opacity: initial?.opacity ?? 0.2,
        border_color: initial?.border_color || "",
        label_visible: initial?.label_visible !== false,
        order: initial?.order ?? 0,
    }));
    const [saving, setSaving] = useState(false);

    const submit = async (e) => {
        e.preventDefault();
        setSaving(true);
        try {
            const payload = { ...form, label: form.label.trim() };
            if (isEdit) {
                await api.put(`/map/zones/${initial.id}`, payload);
                toast.success("Strefa zaktualizowana.");
            } else {
                await api.post("/map/zones", payload);
                toast.success("Strefa dodana.");
            }
            onSaved();
        } catch (err) {
            toast.error(formatApiErrorDetail(err.response?.data?.detail));
        } finally { setSaving(false); }
    };

    return (
        <form onSubmit={submit} className="space-y-4" data-testid="map-zone-form">
            <div className="space-y-2">
                <Label>Etykieta *</Label>
                <Input required value={form.label} onChange={(e) => setForm({ ...form, label: e.target.value })} placeholder="Np. Trawnik wschodni" />
            </div>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-2">
                <div className="space-y-1">
                    <Label className="text-xs">left %</Label>
                    <Input type="number" step="any" min={0} max={100} value={form.left} onChange={(e) => setForm({ ...form, left: parseFloat(e.target.value) || 0 })} />
                </div>
                <div className="space-y-1">
                    <Label className="text-xs">top %</Label>
                    <Input type="number" step="any" min={0} max={100} value={form.top} onChange={(e) => setForm({ ...form, top: parseFloat(e.target.value) || 0 })} />
                </div>
                <div className="space-y-1">
                    <Label className="text-xs">szerokość %</Label>
                    <Input type="number" step="any" min={0.1} max={100} value={form.width} onChange={(e) => setForm({ ...form, width: parseFloat(e.target.value) || 0 })} />
                </div>
                <div className="space-y-1">
                    <Label className="text-xs">wysokość %</Label>
                    <Input type="number" step="any" min={0.1} max={100} value={form.height} onChange={(e) => setForm({ ...form, height: parseFloat(e.target.value) || 0 })} />
                </div>
            </div>
            <div className="grid md:grid-cols-3 gap-3 items-end">
                <div className="space-y-2">
                    <Label>Kolor wypełnienia</Label>
                    <div className="flex items-center gap-2">
                        <input type="color" value={form.color} onChange={(e) => setForm({ ...form, color: e.target.value })} className="h-10 w-12 rounded border border-gray-200" />
                        <Input value={form.color} onChange={(e) => setForm({ ...form, color: e.target.value })} className="flex-1 font-mono text-xs" />
                    </div>
                </div>
                <div className="space-y-2">
                    <Label>Przezroczystość ({Math.round(form.opacity * 100)}%)</Label>
                    <input type="range" min={0} max={1} step={0.05} value={form.opacity} onChange={(e) => setForm({ ...form, opacity: parseFloat(e.target.value) })} className="w-full accent-[#0D4F3C]" />
                </div>
                <div className="space-y-2">
                    <Label>Kolor obramowania (opcjonalnie)</Label>
                    <div className="flex items-center gap-2">
                        <input type="color" value={form.border_color || "#0D4F3C"} onChange={(e) => setForm({ ...form, border_color: e.target.value })} className="h-10 w-12 rounded border border-gray-200" />
                        <Input value={form.border_color} onChange={(e) => setForm({ ...form, border_color: e.target.value })} placeholder="puste = bez obramowania" className="flex-1 font-mono text-xs" />
                    </div>
                </div>
            </div>
            <label className="flex items-center gap-2 p-3 rounded-xl border border-gray-200 cursor-pointer hover:border-[#0D4F3C]/40">
                <input type="checkbox" checked={form.label_visible} onChange={(e) => setForm({ ...form, label_visible: e.target.checked })} className="w-4 h-4 accent-[#0D4F3C]" />
                <span className="text-sm">Pokaż etykietę nad strefą</span>
            </label>
            <div className="flex justify-end gap-2 pt-2 border-t border-gray-50">
                <Button type="button" variant="outline" onClick={onCancel} className="rounded-full">
                    <X size={14} className="mr-1.5" /> Anuluj
                </Button>
                <Button type="submit" disabled={saving} className="rounded-full bg-[#0D4F3C] hover:bg-[#092E22] text-white">
                    <Save size={14} className="mr-1.5" /> {saving ? "Zapisywanie..." : (isEdit ? "Zapisz" : "Dodaj strefę")}
                </Button>
            </div>
        </form>
    );
}

// =====================================================================
// Path editor
// =====================================================================
function PathForm({ initial, draftPoints, onResetPoints, onCancel, onSaved }) {
    const isEdit = !!initial?.id;
    const [form, setForm] = useState(() => ({
        label: initial?.label || "",
        color: initial?.color || "#DC2626",
        stroke_width: initial?.stroke_width ?? 3,
        has_arrow: initial?.has_arrow !== false,
        dash: !!initial?.dash,
        order: initial?.order ?? 0,
    }));
    const [saving, setSaving] = useState(false);

    const effectivePoints = draftPoints || [];

    const submit = async (e) => {
        e.preventDefault();
        if (effectivePoints.length < 2) { toast.error("Ścieżka musi mieć co najmniej 2 punkty."); return; }
        setSaving(true);
        try {
            const payload = {
                label: form.label.trim() || "Ścieżka",
                color: form.color,
                stroke_width: parseInt(form.stroke_width) || 3,
                has_arrow: !!form.has_arrow,
                dash: !!form.dash,
                order: form.order,
                points: effectivePoints.map((p) => ({ left: p.left, top: p.top })),
            };
            if (isEdit) {
                await api.put(`/map/paths/${initial.id}`, payload);
                toast.success("Ścieżka zaktualizowana.");
            } else {
                await api.post("/map/paths", payload);
                toast.success("Ścieżka dodana.");
            }
            onSaved();
        } catch (err) {
            toast.error(formatApiErrorDetail(err.response?.data?.detail));
        } finally { setSaving(false); }
    };

    return (
        <form onSubmit={submit} className="space-y-4" data-testid="map-path-form">
            <div className="grid md:grid-cols-2 gap-3">
                <div className="space-y-2">
                    <Label>Etykieta *</Label>
                    <Input required value={form.label} onChange={(e) => setForm({ ...form, label: e.target.value })} placeholder="Np. Droga ewakuacyjna" />
                </div>
                <div className="space-y-2">
                    <Label>Kolor linii</Label>
                    <div className="flex items-center gap-2">
                        <input type="color" value={form.color} onChange={(e) => setForm({ ...form, color: e.target.value })} className="h-10 w-12 rounded border border-gray-200" />
                        <Input value={form.color} onChange={(e) => setForm({ ...form, color: e.target.value })} className="flex-1 font-mono text-xs" />
                    </div>
                </div>
            </div>
            <div className="grid md:grid-cols-3 gap-3">
                <div className="space-y-2">
                    <Label>Grubość linii ({form.stroke_width})</Label>
                    <input type="range" min={1} max={20} value={form.stroke_width} onChange={(e) => setForm({ ...form, stroke_width: parseInt(e.target.value) })} className="w-full accent-[#0D4F3C]" />
                </div>
                <label className="flex items-center gap-2 p-3 rounded-xl border border-gray-200 cursor-pointer hover:border-[#0D4F3C]/40">
                    <input type="checkbox" checked={form.has_arrow} onChange={(e) => setForm({ ...form, has_arrow: e.target.checked })} className="w-4 h-4 accent-[#0D4F3C]" />
                    <span className="text-sm">Strzałka na końcu</span>
                </label>
                <label className="flex items-center gap-2 p-3 rounded-xl border border-gray-200 cursor-pointer hover:border-[#0D4F3C]/40">
                    <input type="checkbox" checked={form.dash} onChange={(e) => setForm({ ...form, dash: e.target.checked })} className="w-4 h-4 accent-[#0D4F3C]" />
                    <span className="text-sm">Linia przerywana</span>
                </label>
            </div>
            <div className="p-4 bg-gray-50 rounded-2xl border border-gray-100 space-y-2">
                <div className="flex items-center justify-between gap-2">
                    <Label className="m-0">Punkty trasy ({effectivePoints.length})</Label>
                    <Button type="button" variant="outline" size="sm" onClick={onResetPoints} className="rounded-full text-xs text-red-600 hover:bg-red-50 border-red-200">
                        <Trash2 size={12} className="mr-1" /> Wyczyść
                    </Button>
                </div>
                <p className="text-xs text-gray-500">Klikaj na mapę po lewej, aby dodawać kolejne punkty trasy. Minimum 2 punkty.</p>
                {effectivePoints.length > 0 && (
                    <ol className="text-[11px] font-mono text-gray-600 max-h-24 overflow-y-auto space-y-0.5">
                        {effectivePoints.map((p, i) => (
                            <li key={i}>#{i + 1}: left {p.left.toFixed(1)}% / top {p.top.toFixed(1)}%</li>
                        ))}
                    </ol>
                )}
            </div>
            <div className="flex justify-end gap-2 pt-2 border-t border-gray-50">
                <Button type="button" variant="outline" onClick={onCancel} className="rounded-full">
                    <X size={14} className="mr-1.5" /> Anuluj
                </Button>
                <Button type="submit" disabled={saving} className="rounded-full bg-[#0D4F3C] hover:bg-[#092E22] text-white">
                    <Save size={14} className="mr-1.5" /> {saving ? "Zapisywanie..." : (isEdit ? "Zapisz" : "Dodaj ścieżkę")}
                </Button>
            </div>
        </form>
    );
}

// =====================================================================
// Settings (categories + map image)
// =====================================================================
function MapSettings({ settings, onSaved }) {
    const [form, setForm] = useState(settings);
    const [saving, setSaving] = useState(false);
    const [uploading, setUploading] = useState(false);

    useEffect(() => { setForm(settings); }, [settings]);

    const addCategory = () => setForm((f) => ({ ...f, categories: [...(f.categories || []), { key: "", label: "", color: "#0D4F3C", icon: "MapPin" }] }));
    const removeCategory = (i) => setForm((f) => ({ ...f, categories: f.categories.filter((_, idx) => idx !== i) }));
    const updateCategory = (i, patch) => setForm((f) => ({ ...f, categories: f.categories.map((c, idx) => idx === i ? { ...c, ...patch } : c) }));

    const save = async () => {
        // Validate categories
        const clean = (form.categories || []).map((c) => ({
            key: (c.key || "").trim().toLowerCase().replace(/\s+/g, "-"),
            label: (c.label || "").trim(),
            color: c.color || "#0D4F3C",
            icon: c.icon || "MapPin",
        })).filter((c) => c.key && c.label);
        // Dedupe keys
        const keys = new Set();
        for (const c of clean) {
            if (keys.has(c.key)) { toast.error(`Kategorie mają duplikat klucza: ${c.key}`); return; }
            keys.add(c.key);
        }
        setSaving(true);
        try {
            await api.put("/map/settings", {
                image_url: form.image_url || "",
                image_webp_url: form.image_webp_url || "",
                categories: clean,
            });
            toast.success("Ustawienia mapy zapisane.");
            onSaved();
        } catch (err) {
            toast.error(formatApiErrorDetail(err.response?.data?.detail));
        } finally { setSaving(false); }
    };

    const upload = async (e) => {
        const file = e.target.files?.[0];
        if (!file) return;
        setUploading(true);
        try {
            const fd = new FormData();
            fd.append("file", file);
            const r = await api.post("/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
            const newImageUrl = r.data.url;
            // Optimistically update form, then auto-save settings so the public page sees the new image right away.
            const updated = { ...form, image_url: newImageUrl, image_webp_url: "" };
            setForm(updated);
            const clean = (updated.categories || []).map((c) => ({
                key: (c.key || "").trim().toLowerCase().replace(/\s+/g, "-"),
                label: (c.label || "").trim(),
                color: c.color || "#0D4F3C",
                icon: c.icon || "MapPin",
            })).filter((c) => c.key && c.label);
            await api.put("/map/settings", {
                image_url: newImageUrl,
                image_webp_url: "",
                categories: clean,
            });
            toast.success("Nowa mapa wgrana i zapisana. Odśwież stronę /mapa, aby zobaczyć zmianę.");
            onSaved();
        } catch (err) {
            toast.error(formatApiErrorDetail(err.response?.data?.detail));
        } finally { setUploading(false); e.target.value = ""; }
    };

    return (
        <div className="space-y-6">
            <div className="space-y-3">
                <div>
                    <Label>Obraz mapy</Label>
                    <p className="text-xs text-gray-500 mt-1">Domyślnie używane jest <code>/mapa-osiedla.jpg</code>. Możesz wgrać nowy plan katastralny (JPG/PNG/WebP, do 15 MB).</p>
                </div>
                <div className="grid md:grid-cols-2 gap-3">
                    <div className="space-y-2">
                        <Label className="text-xs">URL obrazu (JPG/PNG)</Label>
                        <Input value={form.image_url || ""} onChange={(e) => setForm({ ...form, image_url: e.target.value })} placeholder="/mapa-osiedla.jpg lub /api/files/..." data-testid="map-settings-image-url" />
                    </div>
                    <div className="space-y-2">
                        <Label className="text-xs">URL wersji WebP (opcjonalnie)</Label>
                        <Input value={form.image_webp_url || ""} onChange={(e) => setForm({ ...form, image_webp_url: e.target.value })} placeholder="/mapa-osiedla.webp" />
                    </div>
                </div>
                <div className="flex items-center gap-2">
                    <input id="map-image-upload" type="file" accept="image/jpeg,image/png,image/webp" onChange={upload} className="hidden" />
                    <label htmlFor="map-image-upload" className={`inline-flex items-center gap-2 px-4 py-2 rounded-full text-sm font-medium cursor-pointer border ${uploading ? "opacity-60 pointer-events-none" : "border-[#0D4F3C] text-[#0D4F3C] hover:bg-[#0D4F3C] hover:text-white"} transition-colors`}>
                        <Plus size={14} /> {uploading ? "Wysyłam..." : "Wgraj nową mapę"}
                    </label>
                </div>
            </div>

            <div className="space-y-3 border-t border-gray-100 pt-6">
                <div className="flex items-center justify-between gap-2">
                    <div>
                        <Label>Kategorie punktów</Label>
                        <p className="text-xs text-gray-500 mt-1">Służą do filtrowania POI na stronie publicznej.</p>
                    </div>
                    <Button type="button" variant="outline" size="sm" className="rounded-full" onClick={addCategory} data-testid="map-add-category">
                        <Plus size={12} className="mr-1" /> Dodaj kategorię
                    </Button>
                </div>
                <div className="space-y-2" data-testid="map-categories-list">
                    {(form.categories || []).length === 0 && (
                        <p className="text-sm text-gray-400 italic">Brak kategorii — dodaj pierwszą, aby umożliwić filtrowanie punktów.</p>
                    )}
                    {(form.categories || []).map((c, i) => {
                        const Icon = resolveIcon(c.icon);
                        return (
                            <div key={i} className="grid grid-cols-12 gap-2 items-center p-2 bg-gray-50 rounded-xl">
                                <Input value={c.key} onChange={(e) => updateCategory(i, { key: e.target.value })} placeholder="klucz" className="col-span-3 text-xs" />
                                <Input value={c.label} onChange={(e) => updateCategory(i, { label: e.target.value })} placeholder="Etykieta" className="col-span-4 text-xs" />
                                <input type="color" value={c.color} onChange={(e) => updateCategory(i, { color: e.target.value })} className="h-9 w-9 rounded border border-gray-200 col-span-1" />
                                <select value={c.icon} onChange={(e) => updateCategory(i, { icon: e.target.value })} className="h-9 rounded-md border border-input bg-white px-2 text-xs col-span-3">
                                    {ICON_NAMES.map((n) => <option key={n} value={n}>{n}</option>)}
                                </select>
                                <div className="col-span-1 flex items-center gap-1 justify-end">
                                    <span className="inline-flex items-center justify-center w-7 h-7 rounded-full ring-2 ring-white shadow" style={{ backgroundColor: c.color }}>
                                        <Icon size={12} className="text-white" />
                                    </span>
                                    <button type="button" onClick={() => removeCategory(i)} className="p-1 rounded text-red-600 hover:bg-red-50">
                                        <Trash2 size={14} />
                                    </button>
                                </div>
                            </div>
                        );
                    })}
                </div>
            </div>

            <div className="flex justify-end pt-2 border-t border-gray-50">
                <Button onClick={save} disabled={saving} className="rounded-full bg-[#0D4F3C] hover:bg-[#092E22] text-white" data-testid="map-settings-save">
                    <Save size={14} className="mr-1.5" /> {saving ? "Zapisywanie..." : "Zapisz ustawienia"}
                </Button>
            </div>
        </div>
    );
}

// =====================================================================
// Main component
// =====================================================================
export default function MapaAdmin() {
    const [subtab, setSubtab] = useState("pois");
    const [pois, setPois] = useState([]);
    const [buildings, setBuildings] = useState([]);
    const [zones, setZones] = useState([]);
    const [paths, setPaths] = useState([]);
    const [settings, setSettings] = useState({ image_url: "/mapa-osiedla.jpg", image_webp_url: "/mapa-osiedla.webp", categories: [] });

    const [editing, setEditing] = useState(null); // null | "new" | item
    const [pickingFromMap, setPickingFromMap] = useState(false);
    const [draftPosition, setDraftPosition] = useState(null);
    const [draftPathPoints, setDraftPathPoints] = useState([]);

    const reload = useCallback(async () => {
        try {
            const [p, b, z, pa, s] = await Promise.all([
                api.get("/map/pois"),
                api.get("/map/buildings"),
                api.get("/map/zones"),
                api.get("/map/paths"),
                api.get("/map/settings"),
            ]);
            setPois(p.data || []);
            setBuildings(b.data || []);
            setZones(z.data || []);
            setPaths(pa.data || []);
            setSettings(s.data || { categories: [] });
        } catch (err) {
            toast.error(formatApiErrorDetail(err.response?.data?.detail));
        }
    }, []);

    useEffect(() => { reload(); }, [reload]);

    // Reset editing state when changing subtab
    useEffect(() => {
        setEditing(null);
        setDraftPosition(null);
        setDraftPathPoints([]);
        setPickingFromMap(false);
    }, [subtab]);

    // Reset path waypoints whenever user opens a different editor
    useEffect(() => {
        if (subtab !== "paths") return;
        if (editing === null) {
            setDraftPathPoints([]);
        } else if (editing === "new") {
            setDraftPathPoints([]);
        } else if (editing && editing.points) {
            setDraftPathPoints(editing.points.map((p) => ({ left: p.left, top: p.top })));
        }
    }, [editing, subtab]);

    const handleMapClick = (pos) => {
        if (subtab === "path" && editing) {
            // Append point to draft path
            setDraftPathPoints((pts) => [...pts, pos]);
        } else if (subtab === "paths" && editing) {
            setDraftPathPoints((pts) => [...pts, pos]);
        } else if ((subtab === "pois" || subtab === "buildings") && pickingFromMap) {
            setDraftPosition(pos);
            setPickingFromMap(false);
        } else if ((subtab === "pois" || subtab === "buildings") && editing === "new") {
            // Auto-set when creating new
            setDraftPosition(pos);
        }
    };

    const handleSelectExisting = (type, item) => {
        if (type === "poi" && subtab === "pois") setEditing(item);
        if (type === "building" && subtab === "buildings") setEditing(item);
        if (type === "zone" && subtab === "zones") setEditing(item);
    };

    const remove = async (kind, id, name) => {
        if (!window.confirm(`Na pewno usunąć "${name}"?`)) return;
        try {
            await api.delete(`/map/${kind}/${id}`);
            toast.success("Usunięto.");
            reload();
        } catch (err) {
            toast.error(formatApiErrorDetail(err.response?.data?.detail));
        }
    };

    const togglePoiVisible = async (poi) => {
        try {
            await api.put(`/map/pois/${poi.id}`, { ...poi, visible: !poi.visible });
            reload();
        } catch (err) {
            toast.error(formatApiErrorDetail(err.response?.data?.detail));
        }
    };

    const reorder = async (kind, item, direction) => {
        try {
            await api.put(`/map/${kind}/${item.id}`, { ...item, order: (item.order || 0) + direction });
            reload();
        } catch (err) {
            toast.error(formatApiErrorDetail(err.response?.data?.detail));
        }
    };

    const currentList = subtab === "pois" ? pois : subtab === "buildings" ? buildings : subtab === "zones" ? zones : subtab === "paths" ? paths : [];

    return (
        <div className="space-y-4">
            {/* Subtabs */}
            <div className="flex flex-wrap gap-2" data-testid="map-subtabs">
                {SUBTABS.map((t) => {
                    const I = t.icon;
                    const active = subtab === t.key;
                    return (
                        <button
                            key={t.key}
                            onClick={() => setSubtab(t.key)}
                            className={`inline-flex items-center gap-1.5 px-4 py-1.5 rounded-full text-xs font-medium transition-colors ${
                                active ? "bg-[#0D4F3C] text-white" : "bg-white text-gray-600 border border-gray-200 hover:text-[#0D4F3C]"
                            }`}
                            data-testid={`map-subtab-${t.key}`}
                        >
                            <I size={12} /> {t.label}
                        </button>
                    );
                })}
            </div>

            {subtab === "settings" ? (
                <AdminCard>
                    <h2 className="font-serif text-2xl text-[#092E22] mb-4">Ustawienia mapy</h2>
                    <MapSettings settings={settings} onSaved={reload} />
                </AdminCard>
            ) : (
                <div className="grid lg:grid-cols-5 gap-4">
                    {/* Left: live map preview */}
                    <div className="lg:col-span-3">
                        <AdminCard className="sticky top-4">
                            <div className="flex items-center justify-between mb-3">
                                <h3 className="font-serif text-lg text-[#092E22]">Podgląd mapy</h3>
                                <p className="text-[11px] text-gray-500">
                                    {pickingFromMap && "Tryb wskazywania: kliknij pozycję"}
                                    {editing === "new" && (subtab === "pois" || subtab === "buildings") && !pickingFromMap && "Kliknij na mapę, aby ustawić pozycję"}
                                    {editing && subtab === "paths" && "Kliknij, aby dodać kolejny punkt ścieżki"}
                                </p>
                            </div>
                            <MapPreview
                                imageUrl={settings.image_url}
                                imageWebpUrl={settings.image_webp_url}
                                pois={pois}
                                buildings={buildings}
                                zones={zones}
                                paths={paths}
                                mode={subtab === "pois" ? "poi" : subtab === "buildings" ? "building" : subtab === "zones" ? "zone" : "path"}
                                draftPosition={draftPosition}
                                draftPathPoints={draftPathPoints}
                                onClickMap={handleMapClick}
                                onSelectExisting={handleSelectExisting}
                                highlightId={editing?.id || null}
                            />
                        </AdminCard>
                    </div>

                    {/* Right: list + form */}
                    <div className="lg:col-span-2 space-y-4">
                        <AdminCard>
                            <div className="flex items-center justify-between mb-4">
                                <h3 className="font-serif text-lg text-[#092E22]">
                                    {subtab === "pois" && `Punkty (${pois.length})`}
                                    {subtab === "buildings" && `Budynki (${buildings.length})`}
                                    {subtab === "zones" && `Strefy (${zones.length})`}
                                    {subtab === "paths" && `Ścieżki (${paths.length})`}
                                </h3>
                                {editing === null && (
                                    <Button onClick={() => { setEditing("new"); setDraftPosition(null); setDraftPathPoints([]); }} size="sm" className="rounded-full bg-[#0D4F3C] hover:bg-[#092E22] text-white" data-testid={`map-${subtab}-add`}>
                                        <Plus size={12} className="mr-1" /> Dodaj
                                    </Button>
                                )}
                            </div>

                            {editing ? (
                                <>
                                    {subtab === "pois" && (
                                        <PoiForm
                                            key={editing === "new" ? "new" : editing.id}
                                            initial={editing === "new" ? null : editing}
                                            categories={settings.categories || []}
                                            draftPosition={draftPosition}
                                            onPickFromMap={() => setPickingFromMap(true)}
                                            onCancel={() => { setEditing(null); setDraftPosition(null); }}
                                            onSaved={() => { setEditing(null); setDraftPosition(null); reload(); }}
                                        />
                                    )}
                                    {subtab === "buildings" && (
                                        <BuildingForm
                                            key={editing === "new" ? "new" : editing.id}
                                            initial={editing === "new" ? null : editing}
                                            draftPosition={draftPosition}
                                            onPickFromMap={() => setPickingFromMap(true)}
                                            onCancel={() => { setEditing(null); setDraftPosition(null); }}
                                            onSaved={() => { setEditing(null); setDraftPosition(null); reload(); }}
                                        />
                                    )}
                                    {subtab === "zones" && (
                                        <ZoneForm
                                            key={editing === "new" ? "new" : editing.id}
                                            initial={editing === "new" ? null : editing}
                                            onCancel={() => setEditing(null)}
                                            onSaved={() => { setEditing(null); reload(); }}
                                        />
                                    )}
                                    {subtab === "paths" && (
                                        <PathForm
                                            key={editing === "new" ? "new" : editing.id}
                                            initial={editing === "new" ? null : editing}
                                            draftPoints={draftPathPoints}
                                            onResetPoints={() => setDraftPathPoints([])}
                                            onCancel={() => { setEditing(null); setDraftPathPoints([]); }}
                                            onSaved={() => { setEditing(null); setDraftPathPoints([]); reload(); }}
                                        />
                                    )}
                                </>
                            ) : (
                                <ul className="divide-y divide-gray-100" data-testid={`map-${subtab}-list`}>
                                    {currentList.length === 0 && (
                                        <li className="text-sm text-gray-400 italic py-4 text-center">Brak elementów — dodaj pierwszy.</li>
                                    )}
                                    {currentList.map((item) => {
                                        const Icon = subtab === "pois" ? resolveIcon(item.icon) : null;
                                        return (
                                            <li key={item.id} className="flex items-center gap-2 py-2.5">
                                                <div className="flex-shrink-0">
                                                    {subtab === "pois" && (
                                                        <span className="inline-flex items-center justify-center w-7 h-7 rounded-full ring-1 ring-white shadow" style={{ backgroundColor: item.color }}>
                                                            <Icon size={12} className="text-white" />
                                                        </span>
                                                    )}
                                                    {subtab === "buildings" && (
                                                        <span className="inline-flex items-center justify-center w-7 h-7 rounded-lg bg-[#092E22] text-white text-[10px] font-bold">
                                                            {item.building_id}
                                                        </span>
                                                    )}
                                                    {subtab === "zones" && (
                                                        <span className="inline-block w-7 h-7 rounded-md" style={{ backgroundColor: item.color, opacity: item.opacity }} />
                                                    )}
                                                    {subtab === "paths" && (
                                                        <span className="inline-block w-7 h-1.5 rounded-full" style={{ backgroundColor: item.color }} />
                                                    )}
                                                </div>
                                                <div className="flex-1 min-w-0">
                                                    <p className="text-sm font-medium text-[#092E22] truncate">{subtab === "buildings" ? item.label : item.label}</p>
                                                    <p className="text-[10px] text-gray-400 truncate">
                                                        {subtab === "pois" && `${item.left.toFixed(0)}% / ${item.top.toFixed(0)}% — ${item.status}`}
                                                        {subtab === "buildings" && `${item.apartments} mieszkań · klatki: ${(item.klatki || []).join(", ") || "—"}`}
                                                        {subtab === "zones" && `${item.width.toFixed(0)}×${item.height.toFixed(0)}%`}
                                                        {subtab === "paths" && `${(item.points || []).length} pkt`}
                                                    </p>
                                                </div>
                                                <div className="flex items-center gap-0.5">
                                                    {subtab === "pois" && (
                                                        <button onClick={() => togglePoiVisible(item)} className="p-1.5 rounded hover:bg-gray-100" title={item.visible ? "Ukryj" : "Pokaż"}>
                                                            {item.visible ? <Eye size={14} className="text-gray-500" /> : <EyeOff size={14} className="text-gray-300" />}
                                                        </button>
                                                    )}
                                                    <button onClick={() => reorder(subtab, item, -1)} className="p-1.5 rounded hover:bg-gray-100" title="W górę">
                                                        <ChevronUp size={14} className="text-gray-500" />
                                                    </button>
                                                    <button onClick={() => reorder(subtab, item, 1)} className="p-1.5 rounded hover:bg-gray-100" title="W dół">
                                                        <ChevronDown size={14} className="text-gray-500" />
                                                    </button>
                                                    <button onClick={() => setEditing(item)} className="p-1.5 rounded hover:bg-gray-100" title="Edytuj" data-testid={`map-${subtab}-edit-${item.id}`}>
                                                        <Pencil size={14} className="text-gray-500" />
                                                    </button>
                                                    <button onClick={() => remove(subtab, item.id, item.label || item.building_id)} className="p-1.5 rounded hover:bg-red-50" title="Usuń" data-testid={`map-${subtab}-delete-${item.id}`}>
                                                        <Trash2 size={14} className="text-red-500" />
                                                    </button>
                                                </div>
                                            </li>
                                        );
                                    })}
                                </ul>
                            )}
                        </AdminCard>
                    </div>
                </div>
            )}
        </div>
    );
}
